import type { ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { HeaderUserInfo } from "@/components/HeaderUserInfo";
import { SiteModeSwitcher } from "@/components/SiteModeSwitcher";
import type { SiteMode } from "@/components/siteModes";
import { AlarmInboxBell } from "@/components/AlarmInboxBell";
import { SiteSwitcher } from "@/components/SiteSwitcher";
import { CommandLauncher } from "@/components/CommandLauncher";
import { useIsMobile } from "@/hooks/useIsMobile";
import { HobanLogo, SafersLogo } from "@safers/ui/atoms";
import { areaOf, HOME_PATH, isAreaRoot } from "@/routes/paths";

function Rule({ dark, className = "h-5" }: { dark: boolean; className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={`w-px shrink-0 ${dark ? "bg-white/20" : "bg-black/10"} ${className}`}
    />
  );
}
interface SiteShellHeaderProps {
  title?: string;
  siteName?: string;
  siteId?: string | number;
  current: SiteMode;
  variant?: "light" | "dark";
  back?: boolean;
  switcher?: boolean;
  right?: ReactNode;
  navigationActions?: ReactNode;
  className?: string;
}

export function SiteShellHeader({
  title,
  siteName,
  siteId,
  current,
  variant = "dark",
  back,
  switcher,
  right,
  navigationActions,
  className = "",
}: SiteShellHeaderProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const dark = variant === "dark";
  const isMobile = useIsMobile();

  const showTitle = !!title && areaOf(location.pathname) == null;

  const area = areaOf(location.pathname);
  const showSwitcher = switcher ?? (area != null && area !== "health");
  const showBack = back ?? !isAreaRoot(location.pathname);

  const goBack = () => {
    if (location.key === "default") navigate(HOME_PATH);
    else navigate(-1);
  };

  return (
    <header
      className={`relative z-30 flex h-[calc(3.75rem_+_env(safe-area-inset-top))] shrink-0 items-center justify-between gap-2 px-3 pt-[env(safe-area-inset-top)] sm:gap-3 sm:px-4 ${
        dark
          ? "border-b border-[#2A2D3A] bg-[#0F1117]"
          : "border-b border-black/5 bg-white/90 backdrop-blur-[0.3125rem]"
      } ${className}`}
    >
      <div
        className={`flex min-w-0 items-center gap-2 sm:gap-3 ${navigationActions ? "shrink-0" : ""}`}
      >
        {showBack && (
          <button
            type="button"
            onClick={goBack}
            aria-label="뒤로가기"
            className={`hidden h-9 w-9 shrink-0 items-center justify-center rounded-md transition-colors md:flex ${
              dark
                ? "text-white/60 hover:bg-white/10 hover:text-white"
                : "text-[#55596C] hover:bg-neutral-100"
            }`}
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
        )}

        {isMobile ? (
          <span className="shrink-0 p-1">
            <SafersLogo
              markOnly
              className={`h-[1.625rem] ${dark ? "text-white" : "text-[#1A1D26]"}`}
            />
          </span>
        ) : (
          <button
            type="button"
            onClick={() => navigate(HOME_PATH)}
            aria-label="홈으로"
            className="flex shrink-0 items-center gap-4 rounded-md p-1 transition-opacity hover:opacity-70"
          >
            <SafersLogo
              markOnly
              className={`h-[1.625rem] ${navigationActions ? "lg:hidden" : "sm:hidden"} ${dark ? "text-white" : "text-[#1A1D26]"}`}
            />
            <SafersLogo
              className={`hidden h-[1.625rem] ${navigationActions ? "lg:block" : "sm:block"} ${dark ? "text-white" : "text-[#1A1D26]"}`}
            />
            <span
              className={`hidden shrink-0 items-center gap-2.5 ${navigationActions ? "2xl:flex" : "sm:flex"}`}
            >
              <span
                aria-hidden="true"
                className={`h-4 w-px ${dark ? "bg-white/15" : "bg-black/10"}`}
              />
              <HobanLogo
                variant={dark ? "reverse" : "color"}
                className="h-[1.375rem] text-white/90"
              />
            </span>
          </button>
        )}

        {!isMobile && (
          <div className="flex shrink-0 items-center gap-2 sm:gap-3">
            <Rule dark={dark} className="h-4" />
            <SiteModeSwitcher siteId={siteId} current={current} variant={variant} />
          </div>
        )}
        {navigationActions}
        {(showTitle || showSwitcher) && (
          <>
            <Rule dark={dark} className="h-4" />
            <div className="flex min-w-0 items-center gap-1.5 sm:gap-2">
              {showTitle && (
                <span
                  className={`shrink-0 truncate text-[1.0625rem] font-bold ${
                    dark ? "text-white" : "text-neutral-900"
                  }`}
                >
                  {title}
                </span>
              )}
              {showSwitcher && (
                <SiteSwitcher
                  currentId={siteId == null ? null : Number(siteId)}
                  currentName={siteName}
                  tone={variant}
                />
              )}
            </div>
          </>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-1 sm:gap-2">
        {right}
        {isMobile && <CommandLauncher variant="mobile" tone={variant} />}
        {!isMobile && (
          <>
            {right && <Rule dark={dark} className="mx-1 sm:mx-2" />}
            <CommandLauncher variant="desktop" tone={variant} />
            <span className="flex items-center gap-1 pl-4 sm:gap-2 sm:pl-6">
              <AlarmInboxBell tone={variant} />
              <HeaderUserInfo tone={variant} />
            </span>
          </>
        )}
      </div>
    </header>
  );
}
